import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowUp } from 'lucide-react'
import { useChatBot } from '@/contexts/ChatBotContext'
import { useBackgroundContrast } from '@/hooks/useBackgroundContrast'
import { cn } from '@/lib/utils'

export function BackToTop() {
  const [isVisible, setIsVisible] = useState(false)
  const { t } = useTranslation()
  const { isOpen: isChatBotOpen } = useChatBot()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const background = useBackgroundContrast(buttonRef)

  useEffect(() => {
    const toggleVisibility = () => {
      setIsVisible(window.scrollY > 500)
    }

    window.addEventListener('scroll', toggleVisibility)
    return () => window.removeEventListener('scroll', toggleVisibility)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  const isDarkBackground = background === 'dark'

  return (
    <AnimatePresence>
      {isVisible && !isChatBotOpen && (
        <motion.button
          ref={buttonRef}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.2 }}
          onClick={scrollToTop}
          className={cn(
            'fixed bottom-[74px] right-4 sm:bottom-[92px] sm:right-6 z-tooltip w-10 h-10 sm:w-12 sm:h-12 rounded-full shadow-lg flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2',
            isDarkBackground
              ? 'bg-white text-gray-900 hover:bg-gray-100 focus:ring-white'
              : 'bg-gray-900 text-white hover:bg-gray-700 focus:ring-gray-900'
          )}
          aria-label={t('aria.backToTop')}
        >
          <ArrowUp className="h-4 w-4 sm:h-5 sm:w-5" />
        </motion.button>
      )}
    </AnimatePresence>
  )
}
